import { Icon, Tag } from "@pepperstone/pepper-ui-kit";
import Decimal from "decimal.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CashierPaymentMethod,
  CashierTradingAccount,
  RefundLedgerEntry,
  SavedPaymentAccountRefundSummary,
  SavedPaymentAccountSummary,
  TradingAccountRefundBalance,
  UnlimitableLimitResponse,
} from "@/api/cashier";
import { EnumActionType } from "@/api/cashier";
import type { UploadedFile } from "@/components/bank-statement-upload/BankStatementUpload";
import { ErrorMessage } from "@/components/ErrorMessage";
import { PaymentFormFields } from "@/components/forms/PaymentFormFields";
import { VerticalStepper } from "@/components/general/vertical-stepper/VerticalStepper";
import { SelectableCardSkeleton } from "@/components/selectable-card/SelectableCardSkeleton";
import { Tooltip } from "@/components/tooltip/Tooltip";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { useUserData } from "@/hooks/useUserData";
import {
  CurrencyConversionDropdown,
  type CurrencyConversionOption,
} from "@/pages/withdrawals/choose-method/currency-conversion-dropdown/CurrencyConversionDropdown";
import { PaymentMethodSelectorCard } from "@/pages/withdrawals/choose-method/payment-method-selector-card/PaymentMethodSelectorCard";
import { SavedPaymentAccountCard } from "@/pages/withdrawals/choose-method/saved-payment-account-card/SavedPaymentAccountCard";
import { WithdrawAmountInput } from "@/pages/withdrawals/choose-method/withdraw-amount-input/WithdrawAmountInput";
import type {
  AmountsByMethodId,
  CurrencyConversionBySpaAndCode,
  WithdrawalProfitAmounts,
} from "@/pages/withdrawals/types";
import { useWithdrawalAmounts } from "@/pages/withdrawals/useWithdrawalAmounts";
import {
  getMaxAmount,
  moneyToDecimal,
  moneyToMajorUnits,
} from "@/utils/helpers";
import {
  buildPreselectedRefundView,
  PRESELECTED_REFUND_PM_IDS,
} from "@/utils/refundPreselect";
import { WithdrawRefundAmountInput } from "../withdraw-amount-input/WithdrawRefundAmountInput";
import {
  conversionMinimumInTradingCurrency,
  getProfitConvertedAmount,
  isProfitBelowConversionMinimum,
} from "./conversionLimits";
import methodSelectorStyles from "./MethodSelector.module.css";
import { RefundCardTotalRow } from "./RefundCardTotalRow";
import { RefundEntryRow } from "./RefundEntryRow";
import {
  buildRefundMethodSubTextFromSettings,
  selectRefundDisplaySettings,
  toFeeDisplay,
} from "./refundSettings";

export type FlattenedRefundEntry = Omit<
  SavedPaymentAccountRefundSummary,
  "ledger_entries"
> &
  RefundLedgerEntry;

// TODO refactor this, too much parameters
type MethodSelectorProps = {
  refundAmounts: AmountsByMethodId;
  maxRefunds: CurrencyConversionBySpaAndCode | null;
  balance: TradingAccountRefundBalance | undefined;
  tradingAccount: CashierTradingAccount;
  isLoading?: boolean;
  profitMethods: SavedPaymentAccountSummary[];
  newProfitPaymentMethod: CashierPaymentMethod[];
  initialProfitAmount: Decimal;
  initialRefundAmount: Decimal;
  refundAmountLeft: Decimal;
  onRefundChange: (key: string, amount: Decimal | undefined) => void;
  onNewRefundChange: (key: string, amount: Decimal | undefined) => void;
  onProfitChange?: (changes: WithdrawalProfitAmounts) => void;
  newRefundAmounts?: AmountsByMethodId;
  profitError?: string;
  onError?: (key: string, error: string) => void;
  onProfitConversionChange?: (
    isRequired: boolean,
    selectedCurrency: string | null
  ) => void;
  onRefundConversionChange?: (currencies: Record<string, string>) => void;
  balanceLastUpdated?: number;
  balanceNextUpdate?: number;
  bankStatementFile?: UploadedFile;
  onBankStatementFileChange?: (file: UploadedFile | undefined) => void;
};

export const MethodSelector = ({
  tradingAccount,
  isLoading,
  profitMethods,
  newProfitPaymentMethod,
  initialProfitAmount,
  onProfitChange,
  onRefundChange,
  onNewRefundChange,
  profitError,
  refundAmounts,
  newRefundAmounts,
  maxRefunds,
  balance,
  refundAmountLeft,
  initialRefundAmount,
  onError,
  onProfitConversionChange,
  onRefundConversionChange,
  balanceLastUpdated,
  balanceNextUpdate,
  bankStatementFile,
  onBankStatementFileChange,
}: MethodSelectorProps) => {
  const { t, i18n } = useTranslation();
  const { data: userData } = useUserData();
  const { currencies } = useCurrencies();
  const {
    profitAmounts,
    setProfitAmount,
    clearProfitAmounts,
    getMaximumProfitAmount,
    setNewProfitPaymentMethodId,
    profitAmountLeft,
  } = useWithdrawalAmounts(initialProfitAmount, onProfitChange);

  const [selectedSavedProfitMethod, setSelectedSavedProfitMethod] = useState<
    SavedPaymentAccountSummary | undefined
  >();
  const [selectedNewProfitMethod, setSelectedNewProfitMethod] = useState<
    CashierPaymentMethod | undefined
  >();

  const [profitConversionCurrency, setProfitConversionCurrency] = useState<
    string | null
  >(null);

  const onProfitMethodChange = useCallback(
    (
      savedAccount: SavedPaymentAccountSummary | undefined,
      newProfitMethod: CashierPaymentMethod | undefined
    ) => {
      clearProfitAmounts(); // TODO should we keep the amount from the previous method?
      setSelectedSavedProfitMethod(savedAccount);
      setSelectedNewProfitMethod(newProfitMethod);
      setNewProfitPaymentMethodId(newProfitMethod?.id ?? "");
      setProfitConversionCurrency(null);
    },
    [clearProfitAmounts, setNewProfitPaymentMethodId]
  );

  const onProfitAmountChange = useCallback(
    (value: Decimal | undefined) => {
      if (selectedSavedProfitMethod) {
        setProfitAmount(selectedSavedProfitMethod.id, value, true);
      }
      if (selectedNewProfitMethod) {
        setProfitAmount(selectedNewProfitMethod.id, value, true);
      }
    },
    [setProfitAmount, selectedSavedProfitMethod, selectedNewProfitMethod]
  );

  const isRefundOverAllocated = refundAmountLeft.lt(0);
  // Whether the account actually has any profit balance to withdraw.
  const hasProfitToWithdraw = initialProfitAmount.gt(0);
  const profitWithdrawalAvailable =
    refundAmountLeft.isZero() && hasProfitToWithdraw;

  // If profit becomes unavailable (e.g. balance refresh drops profit to 0)
  // after a method/amount was already selected, clear the stale selection and
  // amounts so we don't keep propagating invalid profit data via onProfitChange.
  const hasStaleProfitSelection =
    !!selectedSavedProfitMethod ||
    !!selectedNewProfitMethod ||
    Object.keys(profitAmounts).length > 0;

  useEffect(() => {
    if (!hasProfitToWithdraw && hasStaleProfitSelection) {
      onProfitMethodChange(undefined, undefined);
    }
  }, [hasProfitToWithdraw, hasStaleProfitSelection, onProfitMethodChange]);

  const loading = isLoading || !currencies;

  // Refund is "not applicable" when there are no refund accounts at all, or
  // when there is no refundable balance to begin with (initialRefundAmount <= 0).
  // NOTE: this intentionally uses the INITIAL refundable amount, not the live
  // `refundAmountLeft`, so that fully allocating (maxing out) the refund does
  // not hide the methods the user is still editing (see PNX-4621).
  const refundNotApplicable =
    !loading &&
    (!balance?.refund_accounts.length || initialRefundAmount.lte(0));

  const { formatAmount } = useFormatAmount();
  const locale = i18n.language;

  const paymentMethodById = useMemo(() => {
    return new Map(newProfitPaymentMethod.map((pm) => [pm.id, pm]));
  }, [newProfitPaymentMethod]);

  // key: `${spaId}_${ledgerId}` → selected currency code for conversion
  const [selectedConversionCurrencies, setSelectedConversionCurrencies] =
    useState<Record<string, string>>({});

  // key: `${spaId}_${depositCode}` → trading-currency equivalent reported by each RefundEntryRow
  const [entryTradingValues, setEntryTradingValues] = useState<
    Record<string, Decimal | null>
  >({});

  const handleEntryTradingValueChange = useCallback(
    (key: string, value: Decimal | null) => {
      setEntryTradingValues((prev) => {
        const current = prev[key];
        if (current === value) return prev;
        if (current && value && current.equals(value)) return prev;
        return { ...prev, [key]: value };
      });
    },
    []
  );

  const requiresCurrencyConversion = useCallback(
    (paymentMethodId: string): boolean => {
      const pm = paymentMethodById.get(paymentMethodId);
      if (!pm || pm.action_type !== EnumActionType.EnumActionTypeBankTransfer)
        return false;
      const supported = Object.keys(pm.settings);
      return (
        supported.length > 0 &&
        !supported.includes(tradingAccount.currency.code)
      );
    },
    [paymentMethodById, tradingAccount.currency.code]
  );

  const getConversionOptions = useCallback(
    (paymentMethodId: string): CurrencyConversionOption[] => {
      const pm = paymentMethodById.get(paymentMethodId);
      if (!pm) return [];
      return Object.entries(pm.settings).map(([code, cfg]) => ({
        code,
        rate: new Decimal(cfg.trading_currency?.exchange_rate?.value ?? 0),
      }));
    },
    [paymentMethodById]
  );

  const savedProfitNeedsConversion =
    selectedSavedProfitMethod != null &&
    selectedSavedProfitMethod.processing_currencies.length > 0 &&
    !selectedSavedProfitMethod.processing_currencies.some(
      ({ code }) => code === tradingAccount.currency.code
    );

  const profitNeedsConversion = selectedNewProfitMethod
    ? requiresCurrencyConversion(selectedNewProfitMethod.id)
    : savedProfitNeedsConversion;

  useEffect(() => {
    onProfitConversionChange?.(
      profitNeedsConversion,
      profitNeedsConversion ? profitConversionCurrency : null
    );
  }, [
    profitNeedsConversion,
    profitConversionCurrency,
    onProfitConversionChange,
  ]);

  const profitExchangeRateAmount = selectedNewProfitMethod
    ? profitAmounts[selectedNewProfitMethod.id]
    : selectedSavedProfitMethod
      ? profitAmounts[selectedSavedProfitMethod.id]
      : undefined;

  const profitConvertedAmount = useMemo<number | null>(() => {
    if (!profitNeedsConversion) return null;
    return getProfitConvertedAmount({
      newMethod: selectedNewProfitMethod,
      savedMethod: selectedSavedProfitMethod,
      conversionCurrencyCode: profitConversionCurrency,
      amount: profitExchangeRateAmount,
    });
  }, [
    profitNeedsConversion,
    profitConversionCurrency,
    profitExchangeRateAmount,
    selectedNewProfitMethod,
    selectedSavedProfitMethod,
  ]);

  const renderProfitTotalRow = () => {
    if (!currencies) return null;
    if (!profitConversionCurrency) return null;
    if (!profitExchangeRateAmount || !profitExchangeRateAmount.gt(0))
      return null;
    if (profitConvertedAmount === null) return null;

    const convertedCurrency = currencies[profitConversionCurrency];
    if (!convertedCurrency) return null;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          paddingBlock: "var(--spacing-xs, 8px)",
          borderTop: "1px solid var(--color-stroke-primary, #03142e1a)",
          marginTop: "8px",
        }}
      >
        <span
          style={{
            fontSize: "var(--typography-size-14, 14px)",
            fontWeight: 600,
            color: "var(--color-text-primary, #03142e)",
            alignSelf: "baseline",
          }}
        >
          {t("withdrawals.methods.currency_conversion.total_withdrawal_label")}
        </span>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "2px",
          }}
        >
          <span
            style={{
              fontSize: "var(--typography-size-14, 14px)",
              fontWeight: 600,
              color: "var(--color-text-primary, #03142e)",
            }}
          >
            {formatAmount(
              profitExchangeRateAmount,
              tradingAccount.currency,
              locale
            )}
          </span>
          <span
            style={{
              fontSize: "var(--typography-size-12, 12px)",
              color: "var(--color-text-tertiary)",
            }}
          >
            ≈{formatAmount(profitConvertedAmount, convertedCurrency, locale)}
          </span>
        </div>
      </div>
    );
  };

  // NEW method: minimum withdrawal for the selected profit receiving currency,
  // denominated in that currency (major units, from the payment-method
  // settings).
  const profitConversionMinimum = useMemo(() => {
    if (!profitNeedsConversion || !profitConversionCurrency) return undefined;
    if (selectedNewProfitMethod) {
      const min =
        selectedNewProfitMethod.settings[profitConversionCurrency]?.limit?.min;
      if (!min || min.unlimited) return undefined;
      return moneyToDecimal(min.value) ?? undefined;
    }
    return undefined;
  }, [
    selectedNewProfitMethod,
    profitNeedsConversion,
    profitConversionCurrency,
  ]);

  // NEW method: the same minimum expressed in the trading currency, for the
  // "(≈ …)" hint in the error. See `conversionMinimumInTradingCurrency`.
  const profitConversionMinimumTrading = useMemo(() => {
    if (selectedNewProfitMethod) {
      return conversionMinimumInTradingCurrency(
        selectedNewProfitMethod,
        profitConversionCurrency,
        profitConversionMinimum
      );
    }
    return null;
  }, [
    selectedNewProfitMethod,
    profitConversionCurrency,
    profitConversionMinimum,
  ]);

  // SAVED method: `minimum_amount` from processing_currencies is already
  // denominated in the trading currency; validate the trading-denominated
  // profit total (the "Total withdrawal") against it directly.
  const savedProfitMinimumTrading = useMemo(() => {
    if (
      !profitNeedsConversion ||
      !profitConversionCurrency ||
      !selectedSavedProfitMethod
    ) {
      return undefined;
    }
    const pc = selectedSavedProfitMethod.processing_currencies.find(
      (c) => c.code === profitConversionCurrency
    );
    return moneyToDecimal(pc?.minimum_amount) ?? undefined;
  }, [
    selectedSavedProfitMethod,
    profitNeedsConversion,
    profitConversionCurrency,
  ]);

  const profitBelowMinimum = selectedSavedProfitMethod
    ? Boolean(
        profitExchangeRateAmount &&
          savedProfitMinimumTrading &&
          profitExchangeRateAmount.gt(0) &&
          profitExchangeRateAmount.lt(savedProfitMinimumTrading)
      )
    : isProfitBelowConversionMinimum(
        profitConvertedAmount,
        profitConversionMinimum
      );

  // Report the profit min-limit error to the parent so it blocks submission.
  // Track the previously reported key to clear it when the method changes, and
  // dedupe by the last reported message so re-running with a non-memoised
  // `onError` (the parent recreates it each render) doesn't loop.
  const prevProfitMinKey = useRef<string | null>(null);
  const lastReportedProfitMin = useRef<string | null>(null);
  useEffect(() => {
    const key = selectedNewProfitMethod
      ? `${selectedNewProfitMethod.id}_conversion_min`
      : selectedSavedProfitMethod
        ? `${selectedSavedProfitMethod.id}_conversion_min`
        : null;
    if (prevProfitMinKey.current !== key) {
      if (prevProfitMinKey.current) onError?.(prevProfitMinKey.current, "");
      lastReportedProfitMin.current = null;
    }
    prevProfitMinKey.current = key;
    if (!key) return;
    const message = !profitBelowMinimum
      ? ""
      : selectedSavedProfitMethod && savedProfitMinimumTrading
        ? t("withdrawals.methods.currency_conversion.error_min_total", {
            amount: formatAmount(
              savedProfitMinimumTrading.toNumber(),
              tradingAccount.currency,
              locale
            ),
            currency: tradingAccount.currency.code,
          })
        : profitConversionCurrency &&
            currencies?.[profitConversionCurrency] &&
            profitConversionMinimum &&
            profitConversionMinimumTrading
          ? t("withdrawals.methods.currency_conversion.error_min", {
              fromAmount: formatAmount(
                profitConversionMinimum.toNumber(),
                currencies[profitConversionCurrency],
                locale
              ),
              fromCurrency: profitConversionCurrency,
              toAmount: formatAmount(
                profitConversionMinimumTrading.toNumber(),
                tradingAccount.currency,
                locale
              ),
              toCurrency: tradingAccount.currency.code,
            })
          : "";
    if (lastReportedProfitMin.current === message) return;
    lastReportedProfitMin.current = message;
    onError?.(key, message);
  }, [
    selectedNewProfitMethod,
    selectedSavedProfitMethod,
    profitBelowMinimum,
    profitConversionCurrency,
    profitConversionMinimum,
    profitConversionMinimumTrading,
    savedProfitMinimumTrading,
    currencies,
    tradingAccount,
    t,
    formatAmount,
    onError,
    locale,
  ]);

  const refundSettingsByPaymentMethodId = useMemo(() => {
    const map = new Map<
      string,
      ReturnType<typeof selectRefundDisplaySettings>
    >();
    for (const pm of newProfitPaymentMethod) {
      map.set(pm.id, selectRefundDisplaySettings(pm));
    }
    return map;
  }, [newProfitPaymentMethod]);

  const refundAccountsWithId =
    balance?.refund_accounts.filter((account) => account.id != null) ?? [];
  const refundAccountsWithoutId = balance?.refund_accounts.filter(
    (account) => !account.id
  );
  const refundAccountsWithoutIdByPaymentMethod =
    refundAccountsWithoutId?.reduce<SavedPaymentAccountRefundSummary[]>(
      (acc, cv) => {
        return acc.some((e) => e.payment_method_id === cv.payment_method_id)
          ? acc
          : acc.concat(cv);
      },
      []
    );
  //map that groups the refund methods by account then by currency, useful for rendering the cards (method) then the inputs (spa currency).
  //Each account now has ledger_entries as sub-items; flatten them to per-currency items for rendering inputs.
  const methodsByCurrencyWithId = refundAccountsWithId.reduce(
    (acc, account) => {
      const key = account.id as string;
      if (!acc[key]) {
        acc[key] = [];
      }
      for (const entry of account.ledger_entries) {
        acc[key].push({
          ...account,
          minimum_amount: entry.minimum_amount,
          maximum_amount: entry.maximum_amount,
          ledger_id: entry.ledger_id,
        });
      }
      return acc;
    },
    {} as Record<string, Array<FlattenedRefundEntry>>
  );

  const methodsByCurrencyWithoutId = refundAccountsWithoutId?.reduce(
    (acc, account) => {
      const key = account.deposit_payment_method_id as string;
      if (!acc[key]) {
        acc[key] = [];
      }
      for (const entry of account.ledger_entries) {
        acc[key].push({
          ...account,
          minimum_amount: entry.minimum_amount,
          maximum_amount: entry.maximum_amount,
          ledger_id: entry.ledger_id,
        });
      }
      return acc;
    },
    {} as Record<string, Array<FlattenedRefundEntry>>
  );

  const getLimitMin = (limit: UnlimitableLimitResponse | undefined) => {
    if (!limit) return undefined;
    if (limit.unlimited) return new Decimal(0);
    const value = moneyToDecimal(limit.value);
    if (value == null) return undefined;
    if (value.isNaN()) return undefined;
    return value;
  };

  const getLimitMax = (
    limit: UnlimitableLimitResponse | undefined,
    amountLeft: Decimal
  ) => {
    if (!limit) return undefined;
    if (limit.unlimited) return amountLeft;
    const value = moneyToDecimal(limit.value);
    if (value == null) return undefined;
    if (value.isNaN()) return undefined;
    const max = value.lt(amountLeft) ? value : amountLeft;

    return max;
  };

  const profitMin = (min: Decimal | undefined, amountLeft: Decimal) => {
    if (!min) return undefined;
    return min.gt(amountLeft) ? amountLeft : min;
  };

  const getCommonMinimumAmount = useCallback(
    (spa: SavedPaymentAccountRefundSummary) => {
      if (spa?.minimum_amount != null) {
        return spa.minimum_amount;
      }

      const processingCurrencyMinimum = spa.processing_currencies?.find(
        (processing_currency) =>
          processing_currency.code === tradingAccount.currency.code
      )?.minimum_amount;
      if (processingCurrencyMinimum != null) {
        return processingCurrencyMinimum;
      }

      const spaId = spa?.id;
      if (!spaId) return undefined;

      const entries = methodsByCurrencyWithId[spaId] ?? [];
      return entries.find(
        (entry) => entry.minimum_amount?.code === tradingAccount.currency.code
      )?.minimum_amount;
    },
    [methodsByCurrencyWithId, tradingAccount.currency.code]
  );

  //Each refund account is already unique per SPA ID with the new grouped structure.
  const uniqueRefundMethods = refundAccountsWithId;

  // Pre-selected refund methods (zota, naewe — see PRESELECTED_REFUND_PM_IDS):
  // render one card per APPROVED saved payment account (from payout_accounts)
  // with the SPA pre-selected, plus one "add new" card. The old saved-account
  // "Select" popover has been removed — no payout-eligible PM uses it.
  const {
    paymentMethodIds: preselectedPaymentMethodIds,
    approvedSpaIdsByPm: approvedRefundSpaIdsByPm,
    ledgerEntriesByPm: ledgerEntriesByPreselectedPm,
  } = buildPreselectedRefundView(balance);

  return (
    <VerticalStepper
      data-testid="withdrawal-method-selector-steps"
      steps={[
        {
          key: "refund",
          title: !refundNotApplicable
            ? t("withdrawals.methods.methods_selector.refund_methods")
            : t(
                "withdrawals.methods.methods_selector.refund_methods_not_applicable"
              ),
          titleContent: (
            <Tooltip
              className="ps-text-body-sm"
              content={t(
                "withdrawals.methods.methods_selector.refund_methods_text"
              )}
              position="right"
            >
              <Icon icon="information" />
            </Tooltip>
          ),
          disabled: loading || refundNotApplicable,
          content: (
            <div
              className={
                refundNotApplicable
                  ? "ps-py-4"
                  : "ps-flex ps-gap-4 ps-flex-wrap ps-py-4 ps-pb-8"
              }
              data-testid="refund-methods"
            >
              {loading ? (
                <>
                  <SelectableCardSkeleton showRadio={false} />
                  <SelectableCardSkeleton showRadio={false} />
                </>
              ) : refundNotApplicable ? null : (
                <>
                  {uniqueRefundMethods?.map((spa) => {
                    if (!spa.id) return null;
                    const isPreselected = PRESELECTED_REFUND_PM_IDS.has(
                      spa.payment_method_id
                    );
                    // Pre-selected PMs render one card per APPROVED saved
                    // account; skip the placeholder and any non-approved
                    // (e.g. pending) refund_account.
                    if (
                      isPreselected &&
                      !approvedRefundSpaIdsByPm
                        .get(spa.payment_method_id)
                        ?.has(spa.id)
                    ) {
                      return null;
                    }
                    const refundSettings = refundSettingsByPaymentMethodId.get(
                      spa.payment_method_id
                    );
                    const effectiveInfoMessage =
                      spa.info_message || refundSettings?.info_message || "";
                    const effectiveProcessingTime =
                      spa.expected_processing_time ||
                      refundSettings?.expected_processing_time ||
                      "";
                    const refundSubText = buildRefundMethodSubTextFromSettings(
                      spa.detail,
                      {
                        expected_processing_time: effectiveProcessingTime,
                        info_message: effectiveInfoMessage,
                      }
                    ).map((line, index) =>
                      typeof line === "string" ? (
                        line
                      ) : (
                        <span
                          key={`refund-extra-${spa.id}-${index}`}
                          className={methodSelectorStyles.refundExtra}
                        >
                          {line.processingTime && (
                            <span>{line.processingTime}</span>
                          )}
                          {line.infoMessage && (
                            <span
                              className={methodSelectorStyles.warningSubText}
                            >
                              {line.infoMessage}
                            </span>
                          )}
                        </span>
                      )
                    );

                    // card-level conversion — one dropdown per card
                    const cardNeedsConversion =
                      spa.processing_currencies.length > 0 &&
                      !spa.processing_currencies.some(
                        ({ code }) => code === tradingAccount.currency.code
                      );
                    const cardConversionKey = spa.id;
                    const cardSelectedCode = cardNeedsConversion
                      ? (selectedConversionCurrencies[cardConversionKey] ??
                        null)
                      : null;
                    // Per-currency exchange rates the backend returns on the
                    // refund summary (code + rate).
                    const cardConversionOptions: CurrencyConversionOption[] =
                      cardNeedsConversion
                        ? spa.processing_currencies.map(({ code, rate }) => ({
                            code: code,
                            rate: new Decimal(rate || 0),
                          }))
                        : [];

                    const entries = methodsByCurrencyWithId?.[spa.id] ?? [];

                    // Sum trading-currency values reported by each RefundEntryRow via callback
                    const totalTrading = entries.reduce((sum, entry) => {
                      const val =
                        entryTradingValues[
                          `${spa.id}_${entry.minimum_amount.code}`
                        ];
                      return val ? sum.add(val) : sum;
                    }, new Decimal(0));

                    // Convert totalTrading → selected receiving currency using the balance rate.
                    // Only needed when selected ≠ trading (otherwise totalTrading IS the received amount).
                    const showConversionTotal =
                      cardSelectedCode &&
                      cardSelectedCode !== tradingAccount.currency.code;
                    let conversionRate: Decimal | null = null;
                    let fromProcessingCurrencies = false;
                    if (showConversionTotal) {
                      for (const entry of entries) {
                        const cb =
                          balance?.currency_conversion_by_asset?.[
                            entry.ledger_id
                          ]?.[cardSelectedCode!];
                        if (cb?.rate && cb.rate !== "0") {
                          conversionRate = new Decimal(cb.rate);
                          break;
                        }
                      }

                      if (!conversionRate) {
                        const processingCurrency =
                          spa.processing_currencies.find(
                            (currency) => currency.code === cardSelectedCode
                          );
                        if (
                          processingCurrency?.rate &&
                          processingCurrency.rate !== "0"
                        ) {
                          conversionRate = new Decimal(processingCurrency.rate);
                          fromProcessingCurrencies = true;
                        }
                      }
                    }

                    const totalConversion =
                      conversionRate && !conversionRate.isZero()
                        ? fromProcessingCurrencies
                          ? totalTrading.mul(conversionRate)
                          : totalTrading.div(conversionRate)
                        : null;

                    // The minimum withdrawal is denominated in the trading
                    // currency. When a receiving currency is selected use that
                    // processing currency's minimum, otherwise fall back to the
                    // account-level common minimum. It is validated against the
                    // summed trading-currency total (the "Total withdrawal").
                    const commonMinimum = getCommonMinimumAmount(spa);
                    const selectedProcessingMinimum = cardSelectedCode
                      ? moneyToDecimal(
                          spa.processing_currencies.find(
                            (currency) => currency.code === cardSelectedCode
                          )?.minimum_amount
                        )
                      : null;
                    const cardMinimumTrading =
                      selectedProcessingMinimum ??
                      (commonMinimum ? moneyToDecimal(commonMinimum) : null);

                    const sharedCardProps = {
                      disabled: refundNotApplicable,
                      title: (
                        <div className="ps-flex ps-gap-2">
                          {spa.name}
                          {entries.length > 1 && (
                            <Tag
                              bgColor="foreground-neutral"
                              label={t(
                                "withdrawals.methods.multiple_currencies"
                              )}
                            />
                          )}
                        </div>
                      ),
                      hideChildSeparatorLine: false,
                      subText: refundSubText,
                      tag:
                        entries.length > 1 ? (
                          <div className="ps-flex ps-flex-col">
                            <div
                              style={{
                                color: "var(--color-text-tertiary)",
                                fontSize: "12px",
                              }}
                            >
                              {t("withdrawals.methods.total_available_amount")}
                            </div>
                            <div
                              style={{
                                color: "var(--color-text-primary, #03142e)",
                                fontWeight: 600,
                              }}
                            >
                              {formatAmount(
                                moneyToMajorUnits(
                                  balance?.ledger_amounts[
                                    spa.ledger_entries[0]?.ledger_id
                                  ]
                                ),
                                tradingAccount.currency,
                                locale
                              )}
                            </div>
                          </div>
                        ) : null,
                      "data-testid": "refund-method-card",
                      selected: Boolean(
                        spa.ledger_entries.some(
                          (entry) =>
                            refundAmounts[
                              `${spa.id}_${entry.minimum_amount.code}`
                            ]
                        )
                      ),
                    } as const;
                    const cardBody = (
                      <div className="ps-p-4 p-w-full">
                        {cardNeedsConversion && (
                          <CurrencyConversionDropdown
                            options={cardConversionOptions}
                            value={cardSelectedCode}
                            onChange={(code) => {
                              const next = {
                                ...selectedConversionCurrencies,
                                [cardConversionKey]: code,
                              };
                              setSelectedConversionCurrencies(next);
                              onRefundConversionChange?.(next);
                            }}
                            tradingCurrencyCode={tradingAccount.currency.code}
                            methodName={spa.name}
                            currencies={currencies}
                          />
                        )}
                        {entries.map((entry, i, arr) => (
                          <RefundEntryRow
                            key={`${spa.id}_${entry.minimum_amount.code}`}
                            spaId={spa.id!}
                            entry={entry}
                            cardNeedsConversion={cardNeedsConversion}
                            cardSelectedCode={cardSelectedCode}
                            tradingAccount={tradingAccount}
                            balance={balance}
                            refundAmounts={refundAmounts}
                            maxRefunds={maxRefunds}
                            refundNotApplicable={refundNotApplicable}
                            currencies={currencies}
                            isLast={i === arr.length - 1}
                            isFirstOfMany={i === 0 && arr.length > 1}
                            onTradingValueChange={handleEntryTradingValueChange}
                            onRefundChange={onRefundChange}
                            locale={locale}
                            onError={(error) =>
                              onError?.(
                                `${spa.id}_${entry.minimum_amount.code}`,
                                error
                              )
                            }
                            lastUpdated={balanceLastUpdated}
                            nextUpdate={balanceNextUpdate}
                          />
                        ))}

                        {/* total row at bottom of card + min-limit check */}
                        <RefundCardTotalRow
                          entriesCount={entries.length}
                          totalTrading={totalTrading}
                          showConversionTotal={Boolean(showConversionTotal)}
                          totalConversion={totalConversion}
                          selectedCode={cardSelectedCode}
                          minimumTrading={cardMinimumTrading}
                          tradingAccount={tradingAccount}
                          currencies={currencies}
                          locale={locale}
                          onError={(error) =>
                            onError?.(`${spa.id}_conversion_min`, error)
                          }
                        />
                      </div>
                    );

                    return (
                      <SavedPaymentAccountCard
                        key={`${spa.id}`}
                        savedPaymentAccount={spa}
                        {...sharedCardProps}
                        fee={spa.fee ? toFeeDisplay(spa.fee) : undefined}
                      >
                        {cardBody}
                      </SavedPaymentAccountCard>
                    );
                  })}
                  {refundAccountsWithoutIdByPaymentMethod?.map(
                    (method, index) => {
                      const paymentMethod = paymentMethodById.get(
                        method.payment_method_id
                      );
                      const refundSettings =
                        refundSettingsByPaymentMethodId.get(
                          method.payment_method_id
                        );
                      const effectiveInfoMessage =
                        method.info_message ||
                        refundSettings?.info_message ||
                        "";
                      const effectiveProcessingTime =
                        method.expected_processing_time ||
                        refundSettings?.expected_processing_time ||
                        "";
                      const refundSubText =
                        buildRefundMethodSubTextFromSettings(method.detail, {
                          expected_processing_time: effectiveProcessingTime,
                          info_message: effectiveInfoMessage,
                        }).map((line, lineIndex) =>
                          typeof line === "string" ? (
                            line
                          ) : (
                            <span
                              key={`refund-extra-${method.deposit_payment_method_id}-${index}-${lineIndex}`}
                              className={methodSelectorStyles.refundExtra}
                            >
                              {line.processingTime && (
                                <span>{line.processingTime}</span>
                              )}
                              {line.infoMessage && (
                                <span
                                  className={
                                    methodSelectorStyles.warningSubText
                                  }
                                >
                                  {line.infoMessage}
                                </span>
                              )}
                            </span>
                          )
                        );
                      const withoutIdEntries =
                        methodsByCurrencyWithoutId?.[
                          method.deposit_payment_method_id as string
                        ] ?? [];
                      const totalWithoutIdTrading = withoutIdEntries.reduce(
                        (sum, entry) => {
                          const entered =
                            newRefundAmounts?.[
                              `${entry.payment_method_id}_${entry.minimum_amount.code}`
                            ];
                          if (!entered) return sum;
                          if (
                            entry.minimum_amount.code ===
                            tradingAccount.currency.code
                          ) {
                            return sum.add(entered);
                          }
                          const conversion =
                            maxRefunds?.[entry.payment_method_id]?.[
                              entry.minimum_amount.code
                            ];
                          if (!conversion || conversion.ratio.isZero())
                            return sum;
                          return sum.add(entered.mul(conversion.ratio));
                        },
                        new Decimal(0)
                      );
                      return (
                        <SavedPaymentAccountCard
                          disabled={refundNotApplicable}
                          title={
                            <>
                              {method.name}
                              {methodsByCurrencyWithoutId
                                ? methodsByCurrencyWithoutId?.[
                                    method.deposit_payment_method_id as string
                                  ]?.length > 1 && (
                                    <Tag
                                      bgColor="foreground-neutral"
                                      label={t(
                                        "withdrawals.methods.multiple_currencies"
                                      )}
                                    />
                                  )
                                : null}
                            </>
                          }
                          hideChildSeparatorLine={false}
                          subText={refundSubText}
                          tag={
                            methodsByCurrencyWithoutId ? (
                              methodsByCurrencyWithoutId?.[
                                method.deposit_payment_method_id as string
                              ]?.length > 1 ? (
                                <div className="ps-flex ps-flex-col">
                                  <div
                                    style={{
                                      color: "var(--color-text-tertiary)",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {t(
                                      "withdrawals.methods.total_available_amount"
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      color:
                                        "var(--color-text-primary, #03142e)",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {formatAmount(
                                      moneyToMajorUnits(
                                        balance?.ledger_amounts[
                                          method.ledger_entries[0]?.ledger_id
                                        ]
                                      ),
                                      tradingAccount.currency
                                    )}
                                  </div>
                                </div>
                              ) : null
                            ) : null
                          }
                          data-testid="refund-method-card"
                          key={`${method.deposit_payment_method_id}_${index}`}
                          savedPaymentAccount={method}
                          fee={toFeeDisplay(method.fee)}
                          selected={Boolean(
                            method.ledger_entries.some(
                              (entry: RefundLedgerEntry) =>
                                refundAmounts[
                                  `${method.deposit_payment_method_id}_${entry.minimum_amount.code}`
                                ]
                            )
                          )}
                        >
                          <div className="ps-p-4 p-w-full">
                            {methodsByCurrencyWithoutId?.[
                              method.deposit_payment_method_id as string
                            ]?.map((method, i, arr) => {
                              const accountAvailable = moneyToMajorUnits(
                                balance?.ledger_amounts?.[method.ledger_id]
                              );
                              const accountHasZeroBalance =
                                accountAvailable === null ||
                                accountAvailable <= 0;
                              return (
                                <div
                                  onPaste={(event) => {
                                    if (
                                      (event.target as Element).matches(
                                        `#refundInput_${method.deposit_payment_method_id}_${method.minimum_amount.code}_${i}`
                                      )
                                    ) {
                                      if (
                                        event.clipboardData
                                          .getData("text/plain")
                                          .includes("-") ||
                                        event.clipboardData
                                          .getData("text/plain")
                                          .includes("e")
                                      ) {
                                        event.preventDefault(); // Block negative signs and scientific notation form
                                      }
                                    }
                                  }}
                                  onBeforeInput={(event) => {
                                    if (
                                      (event.target as Element).matches(
                                        `#refundInput_${method.deposit_payment_method_id}_${method.minimum_amount.code}_${i}`
                                      )
                                    ) {
                                      if (
                                        event.data === "-" ||
                                        event.data === "e"
                                      ) {
                                        event.preventDefault(); // Block negative signs
                                      }
                                    }
                                  }}
                                  key={`${method.deposit_payment_method_id}_${method.minimum_amount.code}_${i}`}
                                >
                                  {i === 0 && arr.length > 1 ? (
                                    <div
                                      style={{
                                        color: "var(--color-text-tertiary)",
                                        fontSize: "12px",
                                        paddingBlockEnd: "16px",
                                      }}
                                    >
                                      {t(
                                        "withdrawals.methods.multiple_currencies_notice"
                                      )}
                                    </div>
                                  ) : null}
                                  <WithdrawRefundAmountInput
                                    disabled={
                                      refundNotApplicable ||
                                      accountHasZeroBalance
                                    }
                                    id={`refundInputNew_${method.payment_method_id}_${method.minimum_amount.code}`}
                                    min={
                                      moneyToDecimal(method.minimum_amount) ??
                                      undefined
                                    }
                                    max={getMaxAmount(
                                      maxRefunds?.[method.payment_method_id]?.[
                                        method.minimum_amount.code
                                      ]
                                    )}
                                    currency={
                                      currencies[method.minimum_amount.code]
                                    }
                                    trading={{
                                      currency: tradingAccount.currency,
                                      amount:
                                        maxRefunds?.[
                                          method.payment_method_id
                                        ]?.[method.minimum_amount.code]
                                          ?.tradingAmount ?? new Decimal(0),
                                    }}
                                    onChange={(amount) =>
                                      onNewRefundChange(
                                        `${method.payment_method_id}_${method.minimum_amount.code}`,
                                        amount
                                      )
                                    }
                                    onError={(error) =>
                                      onError?.(
                                        `${method.payment_method_id}_${method.minimum_amount.code}`,
                                        error
                                      )
                                    }
                                    locale={locale}
                                  />
                                </div>
                              );
                            })}
                            {withoutIdEntries.length > 1 &&
                              totalWithoutIdTrading.gt(0) && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-end",
                                    paddingBlock: "var(--spacing-xs, 8px)",
                                    borderTop:
                                      "1px solid var(--color-stroke-primary, #03142e1a)",
                                    marginTop: "8px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize:
                                        "var(--typography-size-14, 14px)",
                                      fontWeight: 600,
                                      color:
                                        "var(--color-text-primary, #03142e)",
                                    }}
                                  >
                                    {t(
                                      "withdrawals.methods.currency_conversion.total_withdrawal"
                                    )}
                                  </span>
                                  <span
                                    style={{
                                      fontSize:
                                        "var(--typography-size-14, 14px)",
                                      fontWeight: 600,
                                      color:
                                        "var(--color-text-primary, #03142e)",
                                    }}
                                  >
                                    {formatAmount(
                                      totalWithoutIdTrading.toNumber(),
                                      tradingAccount.currency
                                    )}
                                  </span>
                                </div>
                              )}
                            {paymentMethod && userData?.license_id && (
                              <div className="ps-mt-4">
                                <div
                                  style={{
                                    borderTop:
                                      "1px solid var(--color-stroke-primary)",
                                    marginInline:
                                      "calc(-1 * var(--spacing-sm, 16px))",
                                  }}
                                />
                                <div className="ps-mt-4">
                                  <div className="ps-flex ps-gap-2 ps-items-center">
                                    <span>
                                      {/* TODO: replace with translation key */}
                                      {t(
                                        "withdrawals.methods.provide_spa_method_details",
                                        {
                                          method: method.name,
                                        }
                                      )}
                                    </span>
                                    {/* TODO: replace with translation key */}

                                    <Tooltip
                                      content={
                                        //TODO translation key with dynamic values, this is only available for cards, once we have other methods with SPA we need to make this dynamic based on the method type and name
                                        t(
                                          "withdrawals.methods.withdrawal_requirement",
                                          {
                                            original_payment_method: "Card",
                                            required_payment_method: "Card",
                                          }
                                        )
                                      }
                                      position="right"
                                    >
                                      <Icon icon="information" />
                                    </Tooltip>
                                  </div>
                                  <div
                                    style={{
                                      color: "var(--color-text-body )",
                                      fontSize: "12px",
                                      paddingBlockEnd: "16px",
                                    }}
                                  />
                                  <PaymentFormFields
                                    licenceId={userData.license_id}
                                    paymentMethod={paymentMethod}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </SavedPaymentAccountCard>
                      );
                    }
                  )}
                  {preselectedPaymentMethodIds.map((pmId) => {
                    const paymentMethod =
                      balance?.payout_accounts?.[pmId]?.payment_method;
                    if (!paymentMethod || !userData?.license_id) return null;

                    const ledgerEntries =
                      ledgerEntriesByPreselectedPm.get(pmId) ?? [];
                    // Mirror the approved-card minimum check: Zota has no refund
                    // block, so per-entry mins are zeroed by BE and the real
                    // floor is the summary-level common minimum. Validate the
                    // entered total against it via RefundCardTotalRow so Next is
                    // blocked client-side (BE rejects too, but only late).
                    const addNewRepresentative = balance?.refund_accounts.find(
                      (acc) => acc.payment_method_id === pmId
                    );
                    const addNewCommonMinimum = addNewRepresentative
                      ? getCommonMinimumAmount(addNewRepresentative)
                      : undefined;
                    const addNewCommonMinimumDecimal = addNewCommonMinimum
                      ? moneyToDecimal(addNewCommonMinimum)
                      : null;
                    const addNewTotalTrading = ledgerEntries.reduce(
                      (sum, entry) => {
                        const entered =
                          newRefundAmounts?.[
                            `${pmId}_${entry.minimum_amount.code}`
                          ];
                        if (!entered) return sum;
                        if (
                          entry.minimum_amount.code ===
                          tradingAccount.currency.code
                        ) {
                          return sum.add(entered);
                        }
                        const conversion =
                          maxRefunds?.[pmId]?.[entry.minimum_amount.code];
                        if (!conversion || conversion.ratio.isZero())
                          return sum;
                        return sum.add(entered.mul(conversion.ratio));
                      },
                      new Decimal(0)
                    );
                    const addNewSettings =
                      selectRefundDisplaySettings(paymentMethod);
                    const addNewSubText = buildRefundMethodSubTextFromSettings(
                      "",
                      addNewSettings
                    ).map((line, index) =>
                      typeof line === "string" ? (
                        line
                      ) : (
                        <span
                          key={`refund-extra-add-${pmId}-${index}`}
                          className={methodSelectorStyles.refundExtra}
                        >
                          {line.processingTime && (
                            <span>{line.processingTime}</span>
                          )}
                          {line.infoMessage && (
                            <span
                              className={methodSelectorStyles.warningSubText}
                            >
                              {line.infoMessage}
                            </span>
                          )}
                        </span>
                      )
                    );

                    return (
                      <SavedPaymentAccountCard
                        key={`add-new-${pmId}`}
                        disabled={refundNotApplicable}
                        data-testid="refund-method-add-new-card"
                        title={t("withdrawals.methods.add_new_refund_method", {
                          method: paymentMethod.display_name,
                        })}
                        subText={addNewSubText}
                        hideChildSeparatorLine={false}
                        savedPaymentAccount={
                          {
                            id: null,
                            payment_method_id: pmId,
                            name: paymentMethod.display_name,
                            detail: "",
                            logo_url: paymentMethod.logo_url,
                            ledger_entries: ledgerEntries,
                          } as unknown as SavedPaymentAccountRefundSummary
                        }
                        selected={ledgerEntries.some(
                          (entry) =>
                            newRefundAmounts?.[
                              `${pmId}_${entry.minimum_amount.code}`
                            ]
                        )}
                      >
                        <div className="ps-p-4 p-w-full">
                          {ledgerEntries.map((entry) => (
                            <WithdrawRefundAmountInput
                              key={`${pmId}_${entry.minimum_amount.code}`}
                              disabled={refundNotApplicable}
                              id={`refundInputNew_${pmId}_${entry.minimum_amount.code}`}
                              min={
                                moneyToDecimal(entry.minimum_amount) ??
                                undefined
                              }
                              max={getMaxAmount(
                                maxRefunds?.[pmId]?.[entry.minimum_amount.code]
                              )}
                              currency={currencies[entry.minimum_amount.code]}
                              trading={{
                                currency: tradingAccount.currency,
                                amount:
                                  maxRefunds?.[pmId]?.[
                                    entry.minimum_amount.code
                                  ]?.tradingAmount ?? new Decimal(0),
                              }}
                              onChange={(amount) =>
                                onNewRefundChange(
                                  `${pmId}_${entry.minimum_amount.code}`,
                                  amount
                                )
                              }
                              onError={(error) =>
                                onError?.(
                                  `${pmId}_${entry.minimum_amount.code}`,
                                  error
                                )
                              }
                              locale={locale}
                            />
                          ))}
                          {/* min-limit check, mirroring approved cards */}
                          <RefundCardTotalRow
                            entriesCount={ledgerEntries.length}
                            totalTrading={addNewTotalTrading}
                            showConversionTotal={false}
                            totalConversion={null}
                            selectedCode={null}
                            minimumTrading={addNewCommonMinimumDecimal}
                            tradingAccount={tradingAccount}
                            currencies={currencies}
                            locale={locale}
                            onError={(error) =>
                              onError?.(`${pmId}_addnew_conversion_min`, error)
                            }
                          />
                          <div className="ps-mt-4">
                            <PaymentFormFields
                              licenceId={userData.license_id}
                              paymentMethod={paymentMethod}
                            />
                          </div>
                        </div>
                      </SavedPaymentAccountCard>
                    );
                  })}
                </>
              )}
            </div>
          ),
        },
        {
          key: "profit",
          title: t("withdrawals.methods.methods_selector.profit_methods"),
          titleContent: (
            <Tooltip
              className="ps-text-body-sm"
              content={t(
                "withdrawals.methods.methods_selector.profit_methods_text"
              )}
              position="right"
            >
              <Icon icon="information" />
            </Tooltip>
          ),
          disabled: loading || isRefundOverAllocated || !hasProfitToWithdraw,
          content: (
            <div
              className="ps-flex ps-gap-4 ps-flex-wrap ps-py-4 ps-pb-8"
              data-testid="profit-methods"
            >
              {loading ? (
                <SelectableCardSkeleton
                  showRadio={false}
                  className="ps-flex-1 ps-w-1/2"
                />
              ) : (
                <PaymentMethodSelectorCard
                  tradingAccount={tradingAccount}
                  savedPaymentAccounts={profitMethods}
                  paymentMethods={newProfitPaymentMethod}
                  disabled={!profitWithdrawalAvailable}
                  selectedPaymentAccount={selectedSavedProfitMethod}
                  selectedNewPaymentMethod={selectedNewProfitMethod}
                  onChange={onProfitMethodChange}
                  selected={Boolean(
                    (selectedSavedProfitMethod &&
                      profitAmounts[selectedSavedProfitMethod.id]) ||
                      (selectedNewProfitMethod &&
                        profitAmounts[selectedNewProfitMethod.id])
                  )}
                >
                  <div className="ps-p-4 p-w-full">
                    {selectedSavedProfitMethod && (
                      <>
                        {profitNeedsConversion && (
                          <CurrencyConversionDropdown
                            options={selectedSavedProfitMethod.processing_currencies.map(
                              ({ code, rate }) => ({
                                code,
                                rate: new Decimal(rate || 0),
                              })
                            )}
                            value={profitConversionCurrency}
                            onChange={setProfitConversionCurrency}
                            tradingCurrencyCode={tradingAccount.currency.code}
                            methodName={selectedSavedProfitMethod.name}
                            currencies={currencies}
                          />
                        )}
                        <WithdrawAmountInput
                          id={`profit_${selectedSavedProfitMethod.id}`}
                          disabled={
                            !profitWithdrawalAvailable ||
                            (profitNeedsConversion && !profitConversionCurrency)
                          }
                          min={profitMin(
                            // When converting, validate the trading-denominated
                            // profit total against the selected processing
                            // currency's minimum (already in trading currency)
                            // and surface it in the input placeholder.
                            profitNeedsConversion
                              ? savedProfitMinimumTrading
                              : (moneyToDecimal(
                                  selectedSavedProfitMethod.minimum_amount
                                ) ?? undefined),
                            profitAmountLeft
                          )}
                          max={
                            getMaximumProfitAmount({
                              id: selectedSavedProfitMethod.id,
                              max:
                                moneyToDecimal(
                                  selectedSavedProfitMethod.maximum_amount
                                ) ?? undefined,
                            }) || undefined
                          }
                          setInputValue={onProfitAmountChange}
                          currency={
                            currencies[
                              selectedSavedProfitMethod.minimum_amount.code
                            ]
                          }
                          onError={(error) =>
                            onError?.(selectedSavedProfitMethod.id, error)
                          }
                          locale={locale}
                        />
                        {profitNeedsConversion && renderProfitTotalRow()}
                      </>
                    )}
                    {selectedNewProfitMethod && (
                      <>
                        {profitNeedsConversion && (
                          <CurrencyConversionDropdown
                            options={getConversionOptions(
                              selectedNewProfitMethod.id
                            )}
                            value={profitConversionCurrency}
                            onChange={setProfitConversionCurrency}
                            tradingCurrencyCode={tradingAccount.currency.code}
                            methodName={selectedNewProfitMethod.display_name}
                            currencies={currencies}
                          />
                        )}
                        <WithdrawAmountInput
                          id={`profit_new_${selectedNewProfitMethod.id}`}
                          disabled={
                            !profitWithdrawalAvailable ||
                            (profitNeedsConversion && !profitConversionCurrency)
                          }
                          min={(() => {
                            const settings =
                              selectedNewProfitMethod.settings[
                                tradingAccount.currency.code
                              ];
                            if (settings) {
                              return getLimitMin(settings.limit?.min);
                            }
                            if (!profitConversionCurrency) return undefined;
                            const processingSettings =
                              selectedNewProfitMethod.settings[
                                profitConversionCurrency
                              ];
                            const tradingMin =
                              processingSettings?.trading_currency?.limit
                                ?.min || null;
                            return moneyToDecimal(tradingMin) ?? undefined;
                          })()}
                          max={
                            getMaximumProfitAmount({
                              id: selectedNewProfitMethod.id,
                              max: (() => {
                                const settings =
                                  selectedNewProfitMethod.settings[
                                    tradingAccount.currency.code
                                  ];
                                if (settings) {
                                  return getLimitMax(
                                    settings.limit?.max,
                                    profitAmountLeft
                                  );
                                }
                                if (!profitConversionCurrency) return undefined;
                                const processingSettings =
                                  selectedNewProfitMethod.settings[
                                    profitConversionCurrency
                                  ];
                                const tradingMax =
                                  processingSettings?.trading_currency?.limit
                                    ?.max || null;
                                if (tradingMax == null) return profitAmountLeft;
                                const maxDecimal = moneyToDecimal(tradingMax);
                                if (!maxDecimal) return profitAmountLeft;
                                return maxDecimal.lt(profitAmountLeft)
                                  ? maxDecimal
                                  : profitAmountLeft;
                              })(),
                            }) || undefined
                          }
                          setInputValue={onProfitAmountChange}
                          currency={tradingAccount.currency}
                          onError={(error) => {
                            onError?.(selectedNewProfitMethod.id, error);
                          }}
                          locale={locale}
                        />
                        {profitNeedsConversion && renderProfitTotalRow()}
                        <PaymentFormFields
                          licenceId={userData.license_id}
                          paymentMethod={selectedNewProfitMethod}
                          bankStatementFile={bankStatementFile}
                          onBankStatementFileChange={onBankStatementFileChange}
                        />
                        {profitError && (
                          <div className="ps-mb-4">
                            <ErrorMessage message={profitError} />
                          </div>
                        )}
                      </>
                    )}
                    {!selectedSavedProfitMethod && !selectedNewProfitMethod && (
                      <WithdrawAmountInput
                        disabled
                        currency={tradingAccount.currency}
                        min={new Decimal(0)}
                        max={
                          initialProfitAmount.isZero()
                            ? undefined
                            : initialProfitAmount
                        }
                        onError={(error) =>
                          onError?.("profit_initial_amount", error)
                        }
                        locale={locale}
                      />
                    )}
                  </div>
                </PaymentMethodSelectorCard>
              )}
            </div>
          ),
        },
      ]}
    />
  );
};
